import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Trash, ChatCircle, SignOut, User, CaretDown, Sparkle, MagnifyingGlass, X, PencilSimple, Check, GearSix, DownloadSimple, PushPin, Users, Lightning, CurrencyDollar } from '@phosphor-icons/react';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function groupConvos(list) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const week = new Date(today.getTime() - 7 * 86400000);
  const month = new Date(today.getTime() - 30 * 86400000);
  const g = { pinned: [], today: [], week: [], month: [], older: [] };
  list.forEach(c => {
    if (c.pinned) { g.pinned.push(c); return; }
    const d = new Date(c.updated_at || c.created_at);
    if (d >= today) g.today.push(c);
    else if (d >= week) g.week.push(c);
    else if (d >= month) g.month.push(c);
    else g.older.push(c);
  });
  return g;
}

export default function Sidebar({ conversations, activeConvo, onSelect, onDelete, onNew, onRename, onPin, user, onLogout, searchRef }) {
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults(null); return; }
    try {
      const { data } = await axios.get(`${API}/conversations/search?q=${encodeURIComponent(q)}`);
      setSearchResults(data);
    } catch (e) { console.error(e); }
  }, []);

  const handleSearchChange = (val) => {
    setSearch(val);
    if (!val.trim()) { setSearchResults(null); return; }
    // Debounce
    clearTimeout(window._searchTimer);
    window._searchTimer = setTimeout(() => doSearch(val), 250);
  };

  const clearSearch = () => { setSearch(''); setSearchResults(null); };

  const displayList = searchResults !== null ? searchResults : conversations;
  const g = groupConvos(displayList);

  const renderGroup = (label, items, start) => {
    if (!items.length) return null;
    return (
      <div className="mb-3" key={label}>
        <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-white/40 px-3 mb-1">{label}</p>
        {items.map((c, i) => {
          const idx = start + i;
          return <ConvoItem key={c.id} convo={c} idx={idx} active={activeConvo === c.id} onSelect={() => { onSelect(c.id); clearSearch(); }} onDelete={() => onDelete(c.id)} onRename={onRename} onPin={onPin} />;
        })}
      </div>
    );
  };

  let i = 0;
  const ps = i; i += g.pinned.length;
  const ts = i; i += g.today.length;
  const ws = i; i += g.week.length;
  const ms = i; i += g.month.length;
  const os = i;

  return (
    <div className="h-full flex flex-col" data-testid="sidebar">
      {/* Logo */}
      <div className="px-4 pt-4 pb-1 flex items-center gap-2">
        <div className="w-6 h-6 rounded-md bg-lime-400/100 flex items-center justify-center">
          <Sparkle weight="fill" className="w-3 h-3 text-white" />
        </div>
        <span className="text-[15px] font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>Lindey</span>
      </div>

      {/* Search */}
      <div className="px-3 pt-2 pb-1">
        <div className="relative">
          <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
          <input
            ref={searchRef}
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder="Search chats..."
            className="w-full h-8 pl-8 pr-7 rounded-lg bg-white/5 border border-white/10 text-[12px] text-white/80 placeholder:text-white/30 outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400/30 transition-all"
            data-testid="conversation-search-input"
          />
          {search && (
            <button onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80" data-testid="clear-search-button">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* New chat */}
      <div className="px-3 py-1.5">
        <button onClick={onNew} data-testid="sidebar-new-chat"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-medium text-white/50 border border-dashed border-white/10 hover:border-lime-400/40 hover:text-lime-300 hover:bg-lime-400/10 transition-all">
          <Plus className="w-3.5 h-3.5" weight="bold" />
          New chat
        </button>
      </div>

      {/* List */}
      <ScrollArea className="flex-1 px-1.5">
        <div className="px-1 py-1">
          {searchResults !== null && displayList.length === 0 && (
            <div className="flex flex-col items-center py-8 text-center">
              <MagnifyingGlass className="w-5 h-5 text-white/60 mb-2" />
              <p className="text-[11px] text-white/40">No results for "{search}"</p>
            </div>
          )}
          {renderGroup('Pinned', g.pinned, ps)}
          {renderGroup('Today', g.today, ts)}
          {renderGroup('Previous 7 days', g.week, ws)}
          {renderGroup('This month', g.month, ms)}
          {renderGroup('Older', g.older, os)}
          {searchResults === null && conversations.length === 0 && (
            <div className="flex flex-col items-center py-10 text-center">
              <ChatCircle className="w-5 h-5 text-white/60 mb-2" />
              <p className="text-[11px] text-white/40">No conversations yet</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* User */}
      <div className="p-3 border-t border-white/10">
        <Link to="/app/settings" className="flex items-center gap-2 px-2 py-2 rounded-lg text-white/50 hover:bg-white/10 hover:text-white/80 transition-colors mb-1" data-testid="settings-link">
          <GearSix className="w-3.5 h-3.5" />
          <span className="text-[13px]">Settings</span>
        </Link>
        <Link to="/app/import" className="flex items-center gap-2 px-2 py-2 rounded-lg text-white/50 hover:bg-white/10 hover:text-white/80 transition-colors mb-1" data-testid="import-link">
          <DownloadSimple className="w-3.5 h-3.5" />
          <span className="text-[13px]">Import chats</span>
        </Link>
        <Link to="/app/workspaces" className="flex items-center gap-2 px-2 py-2 rounded-lg text-white/50 hover:bg-white/10 hover:text-white/80 transition-colors mb-1" data-testid="workspaces-link">
          <Users className="w-3.5 h-3.5" />
          <span className="text-[13px]">Workspaces</span>
        </Link>
        <Link to="/app/agent-history" className="flex items-center gap-2 px-2 py-2 rounded-lg text-white/50 hover:bg-white/10 hover:text-white/80 transition-colors mb-1" data-testid="agent-history-link">
          <Lightning className="w-3.5 h-3.5" />
          <span className="text-[13px]">Agent history</span>
        </Link>
        {(user?.role === 'admin' || user?.email === 'mrwyattedwards@outlook.com') && (
          <Link to="/app/admin/purchases" className="flex items-center gap-2 px-2 py-2 rounded-lg text-white/50 hover:bg-white/10 hover:text-white/80 transition-colors mb-1" data-testid="admin-purchases-link">
            <CurrencyDollar className="w-3.5 h-3.5" />
            <span className="text-[13px]">Purchases</span>
          </Link>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="user-menu-trigger">
              <div className="w-7 h-7 rounded-lg bg-lime-400/10 border border-lime-400/20 flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-lime-400" />
              </div>
              <span className="text-[13px] font-medium text-white/80 truncate flex-1 text-left">{user?.name || 'User'}</span>
              <CaretDown className="w-3 h-3 text-white/40" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52 rounded-lg">
            <DropdownMenuItem className="text-[13px] text-white/50" data-testid="user-email-display">
              <User className="w-3.5 h-3.5 mr-2" />{user?.email}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-[13px] text-red-400 focus:text-red-400 focus:bg-red-400/10" data-testid="logout-button">
              <SignOut className="w-3.5 h-3.5 mr-2" />Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}


function ConvoItem({ convo, idx, active, onSelect, onDelete, onRename, onPin }) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(convo.title);

  const handleRename = () => {
    if (title.trim() && title !== convo.title) {
      onRename?.(convo.id, title.trim());
    }
    setRenaming(false);
  };

  if (renaming) {
    return (
      <div className="px-3 py-1.5">
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
          onBlur={handleRename}
          className="w-full h-8 px-2 rounded-md border border-lime-400/40 text-[13px] outline-none focus:ring-1 focus:ring-lime-400/30 bg-white/5"
          data-testid={`rename-input-${idx}`}
          autoFocus
        />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.02 }}>
      <button
        onClick={onSelect}
        data-testid={`conversation-item-${idx}`}
        className={`group w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] text-left transition-all ${
          active ? 'bg-lime-400/10 text-white font-medium' : 'text-white/50 hover:bg-white/10 hover:text-white/90'
        }`}
      >
        <ChatCircle className="w-3.5 h-3.5 shrink-0" weight={active ? 'fill' : 'regular'} />
        <span className="truncate flex-1">{convo.title}</span>
        {convo.pinned && <PushPin weight="fill" className="w-2.5 h-2.5 text-lime-500 shrink-0" />}
        <span className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-all">
          <span role="button" tabIndex={0}
            onClick={e => { e.stopPropagation(); onPin?.(convo.id); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); onPin?.(convo.id); } }}
            className={`cursor-pointer ${convo.pinned ? 'text-lime-500 hover:text-lime-400' : 'text-white/40 hover:text-lime-500'}`}
            data-testid={`pin-conversation-${idx}`}>
            <PushPin className="w-3 h-3" weight={convo.pinned ? 'fill' : 'regular'} />
          </span>
          <span role="button" tabIndex={0}
            onClick={e => { e.stopPropagation(); setTitle(convo.title); setRenaming(true); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); setRenaming(true); } }}
            className="text-white/40 hover:text-lime-400 cursor-pointer"
            data-testid={`rename-conversation-${idx}`}>
            <PencilSimple className="w-3 h-3" />
          </span>
          <span role="button" tabIndex={0}
            onClick={e => { e.stopPropagation(); onDelete(); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); onDelete(); } }}
            className="text-white/40 hover:text-red-400 cursor-pointer"
            data-testid={`delete-conversation-${idx}`}>
            <Trash className="w-3 h-3" />
          </span>
        </span>
      </button>
    </motion.div>
  );
}
